import '../product-card-display/product-card-image.display';
import '../product-card-display/product-card-metadata.display';
import store from '../../../store/store';
import { BaseComponent } from '../../base-component/base-component';
import { CtaTypeLabel } from '../product.interface';
import { DesignType } from '../../cart/add-to-cart';
import { PLACEMENT_TOP } from '../../../common/constants/bs';
import { ProductCardData } from '../product-card-display';
import { ViewTypeLabel } from '../../search/interfaces/search.interface';
import { booleanConverter } from '../../../common/converters';
import { connect } from 'pwa-helpers';
import { customElement, property, state } from 'lit/decorators.js';
import { getImageSrcUrl } from '../../../common/utils/product-util';
import { getProductUrl, goToProductPage } from '../utils/product-card.util';
import { html, css, nothing } from 'lit';
import { ifDefined } from 'lit-html/directives/if-defined.js';
import { WishList } from '../../wish-list/interfaces/wish-list-items.interface';
import { emitProductAnalyticsEvent, emitProgramFamilyAnalyticsEvent } from '../utils/product-card.analytics.util';
import { SEG_LOWER, formattedJoin } from '../../../common/utils/util';

@customElement('mhecomm-k12-product-card-display')
export class K12ProductCardDisplay extends connect(store)(BaseComponent) {
  @property()
  cardBootstrapPadding = 'p-3';

  /**
   * Whether the image should be in the center of the card, otherwise it will be on the left.
   */
  @property({ converter: booleanConverter })
  centerCardImage = true;

  @property()
  imageContainerStyle = 'product-card-image-container';

  @property()
  imageStyle = 'product-card-img';

  @property({ type: Object })
  product?: ProductCardData;

  @property({ type: Boolean })
  isWishListProduct: boolean = false;

  @property({ type: Object })
  wishList?: WishList;

  @property()
  addToCartPageType?: string;

  @property({ type: Object })
  programData?: any;

  @property({ converter: booleanConverter })
  showCopyright = true;

  @property({ converter: booleanConverter })
  showEdition = false;

  @property({ converter: booleanConverter })
  showAuthors = false;

  @property({ converter: booleanConverter })
  showISBN = true;

  @property({ converter: booleanConverter })
  showProgramFamily = true;

  @property({ converter: booleanConverter })
  showGrades = true;

  @property({ converter: booleanConverter })
  showPrice = false;

  @property({ converter: booleanConverter })
  showBusinessUnitLabel = false;

  @property({ converter: booleanConverter })
  showBusinessLabelType = true;

  /**
   * Whether to show the CSS box shadow on the product card.
   */
  @property({ converter: booleanConverter })
  showShadow = true;

  @property({ type: String })
  businessLabelText = 'PreK-12';

  @property({ type: String })
  businessLabelType = 'Product';

  @property({ type: String })
  cta = CtaTypeLabel.ADD_TO_CART;

  @property()
  orientation = 'vertical';

  @property({ converter: booleanConverter })
  showProductWishList?: boolean = false;

  @property()
  productWishListPadding = 'p-3';

  /**
   * The position of the add to wish list button on the card (left or right).
   */
  @property()
  wishListButtonPosition: 'left' | 'right' = 'right';

  @state()
  private isWholeCardClickable = false;

  @property({ type: String })
  headingLevel = "h3";

  static styles = [
    css`
      .uxt-mhcorp {
        height: 100%;
      }
    `,
  ];
  
  async firstUpdated(_changedProperties: Map<string | number | symbol, unknown>) {
    super.firstUpdated(_changedProperties);
    this.showProgramFamily = this.showProgramFamily && (this.programData?.id || this.product?.partsOf?.value);
    this.isWholeCardClickable = (this.cta !== CtaTypeLabel.ADD_TO_CART) && !this.showProgramFamily;
  }

  renderComp() {
    return this.product
      ? html` <div class="${this.getCardStyles()}">
          </mhecomm-product-card-image-display>
          <mhecomm-product-card-image-display
            containerStyle="${this.imageContainerStyle}"
            imageStyle="${this.imageStyle}"
            productTitle="${this.product?.title}"
            productImageUrl="${getImageSrcUrl(this.product?.isbn!)}"
          >
          </mhecomm-product-card-image-display>
          <div class="product-card-contents py-3 lh-base d-flex flex-column h-100">
            <mhecomm-product-card-metadata-display
              .product="${this.product}"
              programId="${this.programData?.id || this.product?.partsOf?.value}"
              ?wholecardclickable=${this.isWholeCardClickable}
              .titleTabbable=${this.cta === CtaTypeLabel.NO_CTA || this.cta === CtaTypeLabel.ADD_TO_CART || this.showProgramFamily}
              programTitle="${this.programData?.title || this.product?.partsOf?.label}"
              businessLabelText="${this.businessLabelText}"
              businessLabelType=${this.businessLabelType}
              ?showBusinessUnitLabel=${this.showBusinessUnitLabel}
              ?showBusinessLabelType=${this.showBusinessLabelType}
              ?showProgramFamily=${this.showProgramFamily}
              ?showMHID=${this.showISBN}
              ?showGrades=${this.showGrades}
              ?showEdition=${this.showEdition}
              ?showAuthors=${this.showAuthors}
              ?showCopyright=${this.showCopyright}
              titleHref="${getProductUrl(this.product)}"
              .titleClickListener=${(e: Event) => {
                e.preventDefault();
                const target = e.currentTarget as HTMLAnchorElement;
                emitProductAnalyticsEvent(this, this.product!, SEG_LOWER, 'mhecomm-k12-product-cards');
                window.location.href = target.href;
              }}
              .programFamilyClickListener=${(e: Event) => {
                e.preventDefault();
                const target = e.currentTarget as HTMLAnchorElement;
                emitProgramFamilyAnalyticsEvent(this, this.product!, this.programData!);
                window.location.href = target.href;
              }}
              headingLevel="${this.headingLevel}">
            </mhecomm-product-card-metadata-display>
            ${this.cta !== CtaTypeLabel.NO_CTA ? this.generateProductCardCta() : nothing}
          </div>
          ${this.showProductWishList 
            ? html `
              <div 
                class="position-absolute top-0 ${this.getWishListButtonPositionClass()} ${this.productWishListPadding}">
                <mhecomm-wish-list-update-product-button
                  tooltipPlacement=${PLACEMENT_TOP}
                  .product=${this.product}
                >
                </mhecomm-wish-list-update-product-button>
              </div>`
            : nothing
          }  
        </div>`
      : nothing;
  }

  /**
   * Returns the card styles based on the orientation and other properties.
   */
  getCardStyles(): string {
    return `
      product-card ${this.orientation} rounded text-left border-0 h-100 d-flex position-relative
      ${this.cardBootstrapPadding}
      ${this.centerCardImage ? 'align-items-center': ''}
      ${this.showShadow ? 'shadow' : ''}
      ${this.showProductWishList ? 'pt-4' : ''}
      ${this.isWholeCardClickable ? 'whole-card-clickable focus-within' : ''}
    `;
  }

  generateProductCardCta() {
    let ctaHtml;
    switch (this.cta) {
      case CtaTypeLabel.ADD_TO_CART:
        ctaHtml = html`<mhecomm-add-to-cart
          class="w-100"
          view=${ViewTypeLabel.CARD}
          text="Add to Cart"
          productTitle=${this.product?.title!}
          ?isWishListProduct=${this.isWishListProduct}
          .wishList=${this.wishList!}
          addToCartPageType=${this.addToCartPageType!}
          netPrice=${ifDefined(this.product?.netPrice!)}
          ?showQuantity=${true}
          businessUnit=${SEG_LOWER}
          isbn=${this.product?.isbn! || this.product?.mhid!}
          isbn13=${this.product?.isbn13!}
          .contributors=${formattedJoin(this.product?.contributors!)}
          .copyRightYear=${this.product?.copyRightYear!}
          .edition=${this.product?.edition!}
          .gradeLevel=${this.product?.gradeLevels!}
          designMode=${DesignType.PRIMARY}
          .programSourceId=${this.product?.programSourceId}
        >
        </mhecomm-add-to-cart> `;
        break;
      case CtaTypeLabel.VIEW_ITEM:
      default:
        ctaHtml = html` <button
          class="product-card-view-button btn btn-primary w-100 ${this.isWholeCardClickable ? 'stretched-link' : ''}"
          aria-label="View Product ${this.product?.title}"
          @click="${() => {
            emitProductAnalyticsEvent(this, this.product!, SEG_LOWER, 'mhecomm-k12-product-cards');
            goToProductPage(this.product!);
          }}"
        >
          View Product
        </button>`;
    }
    return html`<div class="product-card-cta-container ${this.cta === CtaTypeLabel.ADD_TO_CART ? '' : 'pt-3'} w-100">
      ${ctaHtml}
    </div>`;
  }

  /**
   * Returns the position class for the wish list button based on the position.
   */
  private getWishListButtonPositionClass(): string {
    return this.wishListButtonPosition === 'left' ? 'start-0' : 'end-0';
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-k12-product-card-display': K12ProductCardDisplay;
  }
}